import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import Loader from "components/Loader";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  AgreementContentRequest,
  AgreementRequest,
  ResponseCode,
  ResponseOfAgreementResponse,
  SaveResponse,
} from "services/KnowfullyAdminClient";
import { Mode, SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import AgreementContentForm from "./AgreementContentForm";
import AgreementDetailsForm from "./AgreementDetailsForm";
import AgreementUserInputForm from "./AgreementUserInputForm";

const getStyles = (theme: Theme): { [key: string]: SxProps } => ({
  expandedPanel: {
    backgroundColor: theme.palette.secondary.light,
  },
});

export type AgreementFormProps = {
  mode: Mode;
};

interface SnackbarLocationState {
  snackbarMessage?: SnackbarMessageProps;
}

const AgreementForm = ({ mode }: AgreementFormProps) => {
  const { id } = useParams<{
    id: string;
  }>();

  const location = useLocation();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const { registerPromise } = useTrashablePromises();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatMessage: t } = useIntl();
  const {
    agreementService,
    agreementEditUrl,
    agreementsUrl,
  } = useGlobalContext();

  const styles = getStyles(useTheme());

  const [locked, lockedActions] = useBoolean(id === undefined);

  const [expandedSection, setExpandedSection] = useState<string | null>(
    "details"
  );

  useEffect(() => {
    const state = location.state as SnackbarLocationState | undefined;
    if (state?.snackbarMessage) {
      setSnackbarMessage(state.snackbarMessage);
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const agreementResponse = useStrictQuery<ResponseOfAgreementResponse>(
    ["agreementService.getAgreement", id],
    () => agreementService.getAgreement(id!),
    {
      enabled: !!id,
    }
  );

  const agreement = agreementResponse.data?.data;
  const title =
    mode === Mode.Create
      ? t({ id: "newAgreement" })
      : agreement?.name || t({ id: "editAgreement" });

  useTitle(title);

  if (agreementResponse.isLoading) return <Loader />;

  if (mode === Mode.Update && !agreement) {
    navigate(agreementsUrl());
  }

  const accordionProps = (section: string) => {
    return {
      expanded: expandedSection === section,
      onChange: toggleExpansion(section),
      disabled: locked && expandedSection !== section,
    };
  };

  const toggleExpansion = (section: string) => (e: any, expanded: boolean) => {
    if (locked || !id) return;
    setExpandedSection(expanded ? section : null);
  };

  const invalidateQueries = (id: string) => {
    queryClient.invalidateQueries(["agreementService.getAgreement", id]);
    queryClient.invalidateQueries(["agreementService.getPlaceholders", id]);
    queryClient.invalidateQueries([
      "getVersionPreview",
      agreement?.templateVersionId,
      agreement?.organizationId,
    ]);
    queryClient.invalidateQueries(["agreementService.getAgreements"]);
  };

  const onCancel = () => {
    if (agreement?.id) {
      invalidateQueries(agreement.id);
    }

    navigate(agreementsUrl());
  };

  const onSaveAgreement = async (values: AgreementRequest) => {
    try {
      const response = await registerPromise(
        agreementService.saveAgreement(values)
      );

      if (response?.responseCode === ResponseCode.OK) {
        invalidateQueries(response.id!);

        if (mode === Mode.Create) {
          lockedActions.setFalse();
          navigate(agreementEditUrl(response.id), {
            state: {
              snackbarMessage: new SnackbarMessageProps(
                t({ id: "agreementCreated" })
              ),
            },
          });

          toggleExpansion("userInput");
        } else {
          setSnackbarMessage(
            new SnackbarMessageProps(t({ id: "agreementUpdated" }))
          );
        }
      }

      return response;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const onContentSubmit = async (values: AgreementRequest) => {
    try {
      const contentRequest = AgreementContentRequest.fromJS({
        templateVersionId: values.templateVersionId,
        content: values.content,
      });

      const response = (await registerPromise(
        agreementService.updateContent(contentRequest)
      )) as SaveResponse;

      if (response?.responseCode === ResponseCode.OK) {
        invalidateQueries(agreement?.id!);

        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "agreementContentSaved" }))
        );
      }
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <>
      <Accordion {...accordionProps("details")}>
        <AccordionSummary
          sx={accordionProps("details").expanded ? styles.expandedPanel : null}
          expandIcon={<ExpandMoreIcon />}
        >
          <Typography>
            <T id="agreementDetailsTitle" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <AgreementDetailsForm
              onSubmit={onSaveAgreement}
              agreement={agreement}
              onCancel={onCancel}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion {...accordionProps("userInput")}>
        <AccordionSummary
          sx={
            accordionProps("userInput").expanded ? styles.expandedPanel : null
          }
          expandIcon={<ExpandMoreIcon />}
        >
          <Typography>
            <T id="agreementUserInputTitle" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <AgreementUserInputForm
              agreement={agreement}
              onSubmit={onSaveAgreement}
              onCancel={onCancel}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion {...accordionProps("content")}>
        <AccordionSummary
          sx={accordionProps("content").expanded ? styles.expandedPanel : null}
          expandIcon={<ExpandMoreIcon />}
        >
          <Typography>
            <T id="agreementContentTitle" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <AgreementContentForm
              agreement={agreement}
              onSubmit={onContentSubmit}
              onCancel={onCancel}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default AgreementForm;
